export const WORKBENCH_PATHS = {
  root: '/workbench',
  review: '/workbench?view=review',
  queue: '/workbench?view=queue',
  caller: '/workbench?view=caller',
  calls: '/workbench?view=calls',
  schedule: '/workbench?view=schedule',
  dispatch: '/workbench?view=dispatch',
  resolve: '/workbench/resolve',
  monitoring: '/workbench/monitoring',
  engineerDay: '/workbench?mode=my-work',
  portfolioChair: '/workbench?mode=portfolio',
  operationsChair: '/workbench?mode=operations',
  activity: '/workbench?view=activity',
  tools: '/workbench?view=tools',
  commandConsole: '/workbench?view=tools&toolTab=console',
  communications: '/workbench?view=communications',
  calendar: '/workbench?view=communications&commTab=calendar',
} as const;

export type WorkbenchView =
  | 'review'
  | 'overview'
  | 'queue'
  | 'caller'
  | 'calls'
  | 'schedule'
  | 'dispatch'
  | 'activity'
  | 'tools'
  | 'communications';
// Calendar only: Hyperion does not ship a general mailbox browser (hyp-4v07).
export type WorkbenchCommunicationTab = 'calendar';
export type DeviceOperationsTab =
  'fleet' | 'patches' | 'remote' | 'network' | 'agents' | 'scripts' | 'mjolnir' | 'mft';
export type AgentOperationsView = 'fleet' | 'deploy' | 'auto' | 'updates' | 'history';
export type MjolnirOperationsView = 'profiles' | 'sessions' | 'playbooks';
export type NetworkOperationsView =
  | 'topology'
  | 'intelligence'
  | 'alerts'
  | 'infrastructure'
  | 'configs'
  | 'credentials'
  | 'documentation';
export type MftSegment = 'overview' | 'jobs' | 'endpoints';
export type ScriptOperationsSegment =
  'library' | 'workflows' | 'executions' | 'rules' | 'schedules' | 'webhooks';
export type MonitoringView = 'alerts' | 'rules' | 'channels' | 'dedupe' | 'history';
export type ResolveView =
  | 'queue'
  | 'incidents'
  | 'alert-rules'
  | 'deduplication'
  | 'correlation'
  | 'escalation'
  | 'channels'
  | 'email'
  | 'security';

export const RESOLVE_VIEWS: readonly ResolveView[] = [
  'queue',
  'incidents',
  'alert-rules',
  'deduplication',
  'correlation',
  'escalation',
  'channels',
  'email',
  'security',
];

export interface RouteLocation {
  pathname: string;
  search?: string;
  hash?: string;
}

const WORKBENCH_VIEWS = new Set<WorkbenchView>([
  'review',
  'overview',
  'queue',
  'caller',
  'calls',
  'schedule',
  'dispatch',
  'activity',
  'tools',
  'communications',
]);

export const DEVICE_OPERATIONS_TABS: readonly DeviceOperationsTab[] = [
  'fleet',
  'patches',
  'remote',
  'network',
  'agents',
  'scripts',
  'mjolnir',
  'mft',
];

/** Query state deliberately shared while moving between Devices capability tabs. */
export const DEVICE_OPERATIONS_SHARED_QUERY_KEYS = [
  'client',
  'q',
  'kind',
  'drawer',
  'drawerTab',
  'drawerItem',
  'deviceGroup',
  'groupBy',
  'sort',
  'dir',
  'columns',
  'rollout',
] as const;

const DEVICE_OPERATIONS_TAB_SET = new Set<DeviceOperationsTab>(DEVICE_OPERATIONS_TABS);
const AGENT_OPERATIONS_VIEW_SET = new Set<AgentOperationsView>([
  'fleet',
  'deploy',
  'auto',
  'updates',
  'history',
]);
const MJOLNIR_OPERATIONS_VIEW_SET = new Set<MjolnirOperationsView>([
  'profiles',
  'sessions',
  'playbooks',
]);
const NETWORK_OPERATIONS_VIEW_SET = new Set<NetworkOperationsView>([
  'topology',
  'intelligence',
  'alerts',
  'infrastructure',
  'configs',
  'credentials',
  'documentation',
]);
const MFT_SEGMENTS = new Set<MftSegment>(['overview', 'jobs', 'endpoints']);
const SCRIPT_OPERATIONS_SEGMENTS = new Set<ScriptOperationsSegment>([
  'library',
  'workflows',
  'executions',
  'rules',
  'schedules',
  'webhooks',
]);

export function deviceOperationsTabFromSearch(search?: string): DeviceOperationsTab {
  const requested = new URLSearchParams(search).get('tab') as DeviceOperationsTab | null;
  return requested && DEVICE_OPERATIONS_TAB_SET.has(requested) ? requested : 'fleet';
}

export function invalidDeviceOperationsTabFromSearch(search?: string): string | null {
  const requested = new URLSearchParams(search).get('tab');
  return requested && !DEVICE_OPERATIONS_TAB_SET.has(requested as DeviceOperationsTab)
    ? requested
    : null;
}

/** Change the Devices capability tab while retaining shared client, filter, and drawer context. */
export function deviceOperationsHref(tab: DeviceOperationsTab, search?: string): string {
  const params = new URLSearchParams(search);
  params.set('tab', tab);
  return `/workbench/devices?${params.toString()}`;
}

/** Open an exact device in the Fleet drawer without discarding the current client scope. */
export function deviceFleetDrawerHref(deviceId: string, search?: string): string {
  const params = new URLSearchParams(search);
  params.set('tab', 'fleet');
  params.set('drawer', `device:${deviceId}`);
  params.delete('drawerTab');
  params.delete('drawerItem');
  return `/workbench/devices?${params.toString()}`;
}

/** Resolve the nested Agents view without colliding with the outer Devices tab. */
export function agentOperationsViewFromLocation(
  pathname: string,
  search?: string
): AgentOperationsView {
  const params = new URLSearchParams(search);
  const requested = params.get('agentView') as AgentOperationsView | null;
  if (requested && AGENT_OPERATIONS_VIEW_SET.has(requested)) return requested;

  // Preserve legacy /agents?tab=... deep links while the specialist deploy,
  // update, and log pages remain mounted as detail workflows.
  if (pathname.includes('/agents')) {
    const legacy = params.get('tab') as AgentOperationsView | null;
    if (legacy && AGENT_OPERATIONS_VIEW_SET.has(legacy)) return legacy;
    if (pathname.endsWith('/auto-provision')) return 'auto';
    if (pathname.endsWith('/updates')) return 'updates';
    if (pathname.endsWith('/deploy')) return 'deploy';
  }
  return 'fleet';
}

/** Change the nested Agents view while preserving Devices client/drawer state. */
export function agentOperationsHref(view: AgentOperationsView, search?: string): string {
  const params = new URLSearchParams(search);
  params.set('tab', 'agents');
  if (view === 'fleet') params.delete('agentView');
  else params.set('agentView', view);
  return `/workbench/devices?${params.toString()}`;
}

/** Open Remote inside the canonical Devices destination, optionally focused on one device. */
export function remoteOperationsHref(search?: string, deviceId?: string | null): string {
  const params = new URLSearchParams(search);
  params.set('tab', 'remote');
  if (deviceId) params.set('device', deviceId);
  else params.delete('device');
  return `/workbench/devices?${params.toString()}`;
}

/** Resolve the nested Mjolnir view without creating another product route. */
export function mjolnirOperationsViewFromSearch(search?: string): MjolnirOperationsView {
  const requested = new URLSearchParams(search).get('mjolnirView') as MjolnirOperationsView | null;
  return requested && MJOLNIR_OPERATIONS_VIEW_SET.has(requested) ? requested : 'profiles';
}

/** Keep browser automation inside Devices while retaining exact client/device context. */
export function mjolnirOperationsHref(
  view: MjolnirOperationsView,
  search?: string,
  profileId?: string | null
): string {
  const params = new URLSearchParams(search);
  params.set('tab', 'mjolnir');
  if (view === 'profiles') params.delete('mjolnirView');
  else params.set('mjolnirView', view);
  if (profileId) params.set('profileId', profileId);
  return `/workbench/devices?${params.toString()}`;
}

/** Resolve the nested Network view while keeping Topology as the compatibility default. */
export function networkOperationsViewFromSearch(search?: string): NetworkOperationsView {
  const requested = new URLSearchParams(search).get('networkView') as NetworkOperationsView | null;
  return requested && NETWORK_OPERATIONS_VIEW_SET.has(requested) ? requested : 'topology';
}

export function invalidNetworkOperationsViewFromSearch(search?: string): string | null {
  const requested = new URLSearchParams(search).get('networkView');
  return requested && !NETWORK_OPERATIONS_VIEW_SET.has(requested as NetworkOperationsView)
    ? requested
    : null;
}

/** Keep every network-monitoring capability in the canonical Devices destination. */
export function networkOperationsHref(view: NetworkOperationsView, search?: string): string {
  const params = new URLSearchParams(search);
  params.set('tab', 'network');
  if (view === 'topology') params.delete('networkView');
  else params.set('networkView', view);
  return `/workbench/devices?${params.toString()}`;
}

export function mftSegmentFromSearch(search?: string): MftSegment {
  const requested = new URLSearchParams(search).get('seg') as MftSegment | null;
  return requested && MFT_SEGMENTS.has(requested) ? requested : 'overview';
}

export function mftOperationsHref(
  segment: MftSegment,
  search?: string,
  drawer?: string | null
): string {
  const params = new URLSearchParams(search);
  params.set('tab', 'mft');
  params.set('seg', segment);
  if (drawer) params.set('drawer', drawer);
  else params.delete('drawer');
  return `/workbench/devices?${params.toString()}`;
}

export function scriptOperationsSegmentFromSearch(search?: string): ScriptOperationsSegment {
  const requested = new URLSearchParams(search).get('seg') as ScriptOperationsSegment | null;
  return requested && SCRIPT_OPERATIONS_SEGMENTS.has(requested) ? requested : 'library';
}

/** Own script operations, execution focus, and script detail inside the Devices destination. */
export function scriptOperationsHref(
  segment: ScriptOperationsSegment,
  search?: string,
  drawer?: string | null
): string {
  const params = new URLSearchParams(search);
  params.set('tab', 'scripts');
  params.set('seg', segment);
  params.delete('view');
  params.delete('run');
  if (drawer) params.set('drawer', drawer);
  else params.delete('drawer');
  return `/workbench/devices?${params.toString()}`;
}

export function workbenchViewFromSearch(search?: string): WorkbenchView {
  const requested = new URLSearchParams(search).get('view') as WorkbenchView | null;
  return requested && WORKBENCH_VIEWS.has(requested) ? requested : 'overview';
}

export function workbenchLocationForView(
  view: WorkbenchView,
  { search, hash }: Pick<RouteLocation, 'search' | 'hash'>
): RouteLocation {
  const params = new URLSearchParams(search);
  if (view === 'overview') params.delete('view');
  else params.set('view', view);

  const nextSearch = params.toString();
  return {
    pathname: WORKBENCH_PATHS.root,
    search: nextSearch ? `?${nextSearch}` : '',
    hash: hash ?? '',
  };
}

function stringValue(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null;
}

export interface WorkbenchEventFocus {
  id: string;
  type: 'ticket-assigned' | 'alert-triggered';
  data?: Record<string, unknown>;
  href?: string;
}

/**
 * Converts an event toast into in-chair focus state instead of navigating the
 * operator away from Workbench. Unknown event payloads still retain their
 * supplied destination.
 */
export function workbenchLocationForEvent(
  event: WorkbenchEventFocus,
  location: Pick<RouteLocation, 'search' | 'hash'>
): RouteLocation | string {
  const data = event.data ?? {};
  const hrefTicketId = event.href?.match(/^\/tickets\/([^/?#]+)/)?.[1];
  const hrefAlertId = event.href?.match(/^\/(?:monitoring\/alerts|alerts)\/([^/?#]+)/)?.[1];

  if (event.type === 'ticket-assigned') {
    const ticketId =
      stringValue(data.ticketId) ??
      stringValue(data.ticket_id) ??
      stringValue(data.id) ??
      hrefTicketId ??
      event.id;
    const next = workbenchLocationForView('queue', location);
    const params = new URLSearchParams(next.search);
    params.set('focusSource', 'ticket');
    params.set('focusId', decodeURIComponent(ticketId));
    return { ...next, search: `?${params.toString()}` };
  }

  if (event.type === 'alert-triggered') {
    const alertId =
      stringValue(data.alertId) ??
      stringValue(data.alert_id) ??
      stringValue(data.id) ??
      hrefAlertId ??
      event.id;
    const params = new URLSearchParams(location.search);
    params.delete('view');
    params.set('alert', decodeURIComponent(alertId));
    return {
      pathname: WORKBENCH_PATHS.monitoring,
      search: `?${params.toString()}`,
      hash: location.hash ?? '',
    };
  }

  return event.href ?? WORKBENCH_PATHS.root;
}

/** Caller lookup, optionally pre-filled with a phone number, email or name. */
export function workbenchCallerHref(query?: string | null): string {
  const params = new URLSearchParams({ view: 'caller' });
  if (query) params.set('q', query);
  return `${WORKBENCH_PATHS.root}?${params.toString()}`;
}

export function workbenchQueueItemHref(source: string, id: string): string {
  const params = new URLSearchParams({ view: 'queue', focusSource: source, focusId: id });
  return `${WORKBENCH_PATHS.root}?${params.toString()}`;
}

export interface WorkbenchTakeLiveInput {
  clientId?: string | null;
  ticketId?: string | null;
  deviceId?: string | null;
  userId?: string | null;
  evidenceSource: string;
  evidenceId?: string | null;
  intendedAction?: string | null;
  returnTo: RouteLocation;
}

export type WorkbenchTakeLiveDestination =
  { kind: 'ready'; href: string } | { kind: 'unavailable'; reason: string };

/**
 * Builds the explicit Resolve-to-Workbench live handoff. Client and evidence
 * are mandatory because choosing either from nearby rows would silently point
 * an operator at the wrong tenant context. Optional targets remain absent when
 * the source did not provide them and are validated by the destination.
 */
export function workbenchTakeLiveDestination(
  input: WorkbenchTakeLiveInput
): WorkbenchTakeLiveDestination {
  if (!input.clientId?.trim()) {
    return {
      kind: 'unavailable',
      reason: 'This queue item does not supply an exact client. No Workbench target was guessed.',
    };
  }
  if (!input.evidenceId?.trim()) {
    return {
      kind: 'unavailable',
      reason:
        'This queue item does not supply an exact source evidence ID. No Workbench target was guessed.',
    };
  }

  const params = new URLSearchParams({
    mode: 'my-work',
    client: input.clientId,
    liveContext: 'resolve',
    evidenceSource: input.evidenceSource,
    evidenceId: input.evidenceId,
  });
  if (input.ticketId?.trim()) params.set('ticket', input.ticketId);
  if (input.deviceId?.trim()) params.set('device', input.deviceId);
  if (input.userId?.trim()) params.set('user', input.userId);
  if (input.intendedAction?.trim()) params.set('action', input.intendedAction);

  const returnTo = `${input.returnTo.pathname}${input.returnTo.search ?? ''}${input.returnTo.hash ?? ''}`;
  params.set('returnTo', returnTo);
  return { kind: 'ready', href: `${WORKBENCH_PATHS.root}?${params.toString()}` };
}

/** Accept only the canonical local Resolve destination as a return link. */
export function safeResolveReturnHref(value?: string | null): string | null {
  if (!value) return null;
  try {
    const parsed = new URL(value, 'https://hyperion.local');
    if (parsed.origin !== 'https://hyperion.local' || parsed.pathname !== WORKBENCH_PATHS.resolve) {
      return null;
    }
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return null;
  }
}

const PRODUCT_ROOTS = ['/workbench', '/runway', '/portfolio', '/library', '/switchboard'];

/** Accept only a same-origin destination inside one of the mounted product roots. */
export function safeProductReturnHref(value?: string | null): string | null {
  if (!value) return null;
  try {
    const parsed = new URL(value, 'https://hyperion.local');
    const isProductPath = PRODUCT_ROOTS.some(
      (root) => parsed.pathname === root || parsed.pathname.startsWith(`${root}/`)
    );
    if (parsed.origin !== 'https://hyperion.local' || !isProductPath) return null;
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return null;
  }
}

export interface WorkbenchTicketsFilter {
  /** Customer id; TicketsV2 reads `client` and sends it as `customer_id`. */
  client?: string | null;
  /** Assignee user id (or `me`). */
  assignee?: string | null;
  /** `create` opens the Smart Ticket rail on arrival. */
  action?: 'create' | null;
}

/** The Workbench ticket list, optionally scoped by client/assignee. */
export function workbenchTicketsHref(filter: WorkbenchTicketsFilter = {}): string {
  const params = new URLSearchParams();
  if (filter.client) params.set('client', filter.client);
  if (filter.assignee) params.set('assignee', filter.assignee);
  if (filter.action) params.set('action', filter.action);
  const search = params.toString();
  return `/workbench/tickets${search ? `?${search}` : ''}`;
}

/** One ticket inside Workbench. There is no root-level /tickets route. */
export function workbenchTicketHref(ticketId: string): string {
  return `/workbench/tickets/${encodeURIComponent(ticketId)}`;
}

/** Security incident list, or one incident's response cockpit, inside Workbench. */
export function workbenchIncidentHref(incidentId?: string | null): string {
  return incidentId
    ? `/workbench/security/incidents/${encodeURIComponent(incidentId)}`
    : '/workbench/security/incidents';
}

/** Remote control for one device (or the Remote tab when no device is known). */
export function workbenchRemoteHref(deviceId?: string | null): string {
  return remoteOperationsHref(undefined, deviceId ?? null);
}

export function workbenchCommandConsoleHref(deviceId?: string): string {
  const params = new URLSearchParams({ view: 'tools', toolTab: 'console' });
  if (deviceId) params.set('device', deviceId);
  return `${WORKBENCH_PATHS.root}?${params.toString()}`;
}

export function workbenchCommunicationsHref(
  tab: WorkbenchCommunicationTab,
  options: { action?: string; eventId?: string } = {}
): string {
  const params = new URLSearchParams({ view: 'communications', commTab: tab });
  if (options.action) params.set('action', options.action);
  if (options.eventId) params.set('event', options.eventId);
  return `${WORKBENCH_PATHS.root}?${params.toString()}`;
}

export function monitoringLocationForView(
  view: MonitoringView,
  { search, hash }: Pick<RouteLocation, 'search' | 'hash'>
): RouteLocation {
  const params = new URLSearchParams(search);
  if (view === 'alerts') params.delete('tab');
  else params.set('tab', view);
  if (view !== 'alerts') params.delete('alert');

  const nextSearch = params.toString();
  return {
    pathname: WORKBENCH_PATHS.monitoring,
    search: nextSearch ? `?${nextSearch}` : '',
    hash: hash ?? '',
  };
}

export function resolveViewFromSearch(search?: string): ResolveView {
  const requested = new URLSearchParams(search).get('tab') as ResolveView | null;
  return requested && RESOLVE_VIEWS.includes(requested) ? requested : 'queue';
}

export function resolveLocationForView(
  view: ResolveView,
  { search, hash }: Pick<RouteLocation, 'search' | 'hash'>
): RouteLocation {
  const params = new URLSearchParams(search);
  if (view === 'queue') params.delete('tab');
  else params.set('tab', view);

  const nextSearch = params.toString();
  return {
    pathname: WORKBENCH_PATHS.resolve,
    search: nextSearch ? `?${nextSearch}` : '',
    hash: hash ?? '',
  };
}
